import { useEffect, useState } from 'react';
import type { MiembroProyecto, UsuarioEquipo } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from './Iconos.js';

/**
 * Quién participa en un proyecto.
 *
 * Ser miembro y tener una tarea asignada son cosas distintas: alguien puede
 * entrar en un proyecto antes de que exista la primera tarea, y hasta ahora la
 * única forma de que apareciera era inventarle una.
 *
 * Sólo se ofrece gente del equipo. Un propietario o un proveedor no entran en
 * un proyecto interno de la gestora, y el servidor lo vuelve a comprobar.
 */
export function MiembrosProyecto({
  idioma,
  proyectoId,
  puedeEditar,
}: {
  idioma: Idioma;
  proyectoId: string;
  puedeEditar: boolean;
}) {
  const [miembros, setMiembros] = useState<MiembroProyecto[] | null>(null);
  const [candidatos, setCandidatos] = useState<UsuarioEquipo[]>([]);
  const [busca, setBusca] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .miembrosProyecto(proyectoId)
      .then((r) => setMiembros(r.miembros))
      .catch(() => setMiembros([]));
    if (puedeEditar) {
      api
        .equipo()
        .then((r) =>
          setCandidatos(
            r.equipo.filter((u) => u.activo && (u.rol === 'DIRECTOR' || u.rol === 'MANAGER')),
          ),
        )
        .catch(() => undefined);
    }
  }, [proyectoId, puedeEditar]);

  const fallo = (f: unknown) =>
    setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'));

  const yaEstan = new Set((miembros ?? []).map((m) => m.usuarioId));
  const q = busca.trim().toLowerCase();
  const sugerencias = candidatos
    .filter((u) => !yaEstan.has(u.id))
    .filter((u) => !q || u.nombre.toLowerCase().includes(q))
    .slice(0, 6);

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>{t(idioma, 'proyecto.miembros')}</h2>
        <span className="chip-area">{(miembros ?? []).length}</span>
      </div>

      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}

      {(miembros ?? []).length === 0 ? (
        <p className="suave">{t(idioma, 'proyecto.sinMiembros')}</p>
      ) : (
        <div className="lista">
          {(miembros ?? []).map((m) => (
            <div key={m.id} className="fila">
              <Icono nombre="personas" />
              <span className="crecer">
                <b>{m.nombre}</b>
                {m.funcion && <small>{t(idioma, `funcion.${m.funcion}`)}</small>}
              </span>
              {m.rol === 'RESPONSABLE' && (
                <span className="pastilla ambar">{t(idioma, 'proyecto.responsable')}</span>
              )}
              {puedeEditar && (
                <button
                  type="button"
                  className="enlace"
                  aria-label={t(idioma, 'equipo.retirar')}
                  onClick={() =>
                    void api
                      .retirarMiembroProyecto(proyectoId, m.usuarioId)
                      .then((r) => setMiembros(r.miembros))
                      .catch(fallo)
                  }
                >
                  <Icono nombre="papelera" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {puedeEditar && (
        <>
          <div className="campo">
            <label htmlFor="miembro-buscar">{t(idioma, 'proyecto.anadirMiembro')}</label>
            <input
              id="miembro-buscar"
              type="search"
              value={busca}
              placeholder={t(idioma, 'equipo.buscar')}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="lista">
            {sugerencias.map((u) => (
              <button
                key={u.id}
                type="button"
                className="fila"
                onClick={() =>
                  void api
                    .anadirMiembroProyecto(proyectoId, u.id)
                    .then((r) => {
                      setMiembros(r.miembros);
                      setBusca('');
                    })
                    .catch(fallo)
                }
              >
                <span aria-hidden="true">＋</span>
                <span className="crecer">{u.nombre}</span>
                {u.funcion && (
                  <span className="chip-area">{t(idioma, `funcion.${u.funcion}`)}</span>
                )}
              </button>
            ))}
            {sugerencias.length === 0 && (
              <p className="suave">{t(idioma, 'proyecto.sinCandidatos')}</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
